import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Package, 
  MagnifyingGlass, 
  Funnel,
  Check,
  X,
  Truck,
  Eye,
  FilePdf,
  MapPin,
  Phone,
  User,
  Clock,
  CurrencyDollar,
  ArrowsClockwise,
  Copy,
  CalendarBlank
} from '@phosphor-icons/react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import SortableTable from '../components/SortableTable';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useSellerMode } from '../contexts/SellerModeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OrdersPage() {
  const { activeSeller } = useSellerMode();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    fulfillment_mode: '',
    search: ''
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [shipDialogOpen, setShipDialogOpen] = useState(false);
  const [shipOrderId, setShipOrderId] = useState(null);
  const [shipForm, setShipForm] = useState({ tracking_number: '', courier: '' });

  // Re-fetch orders when seller mode changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      fetchOrders();
    }, 100);
    return () => clearTimeout(timer);
  }, [filters.status, filters.fulfillment_mode, activeSeller?.user_id]);

  const fetchOrders = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.fulfillment_mode) params.append('fulfillment_mode', filters.fulfillment_mode);
      
      const response = await axios.get(`${API_URL}/api/orders?${params}`, { withCredentials: true });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleOrderAction = async (orderId, action, data = {}) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/orders/${orderId}/${action}`, data, { withCredentials: true });
      toast.success(`Order ${action} successfully`);
      fetchOrders();
      setSelectedOrder(null);
      setOrderDetail(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} order`);
    } finally {
      setActionLoading(false);
    }
  };

  const openOrderDetail = async (order) => {
    setSelectedOrder(order);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/orders/${order.order_id}`, { withCredentials: true });
      setOrderDetail(data);
    } catch {
      setOrderDetail(order); // fallback to list data
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDrawer = () => { setSelectedOrder(null); setOrderDetail(null); };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copied'); };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const downloadInvoice = async (orderId) => {
    try {
      const res = await axios.get(`${API_URL}/api/orders/${orderId}/invoice`, { withCredentials: true });
      const inv = res.data;
      const doc = new jsPDF();
      doc.setFontSize(20);
      doc.setTextColor(125, 100, 255);
      doc.text('INVOICE', 14, 22);
      doc.setFontSize(10);
      doc.setTextColor(90, 92, 135);
      doc.text(inv.invoice_number, 14, 30);
      doc.text(`Date: ${new Date(inv.date).toLocaleDateString()}`, 14, 36);
      doc.text(`Order: ${inv.order_id}`, 14, 42);

      // Seller info
      doc.setFontSize(9);
      doc.text('FROM:', 14, 55);
      doc.setTextColor(10, 10, 10);
      doc.text(inv.seller?.name || 'Seller', 14, 61);
      if (inv.seller?.gst) doc.text(`GST: ${inv.seller.gst}`, 14, 67);
      if (inv.seller?.address) doc.text(inv.seller.address, 14, 73);

      // Customer info
      doc.setTextColor(90, 92, 135);
      doc.text('TO:', 120, 55);
      doc.setTextColor(10, 10, 10);
      doc.text(inv.customer?.name || 'Customer', 120, 61);
      doc.text(`${inv.customer?.city || ''}, ${inv.customer?.state || ''}`, 120, 67);

      // Items table
      doc.autoTable({
        startY: 82,
        head: [['Item', 'SKU', 'Qty', 'Price', 'Total']],
        body: inv.items.map(item => [item.name, item.sku, item.quantity, `Rs.${item.price}`, `Rs.${(item.price * item.quantity).toFixed(2)}`]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [125, 100, 255] },
      });

      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(9);
      doc.text(`Subtotal: Rs.${inv.subtotal?.toFixed(2)}`, 140, finalY);
      doc.text(`GST (18%): Rs.${inv.tax_amount?.toFixed(2)}`, 140, finalY + 6);
      doc.setFontSize(11);
      doc.setTextColor(125, 100, 255);
      doc.text(`Total: Rs.${inv.total?.toFixed(2)}`, 140, finalY + 14);

      doc.save(`${inv.invoice_number}.pdf`);
      toast.success('Invoice downloaded');
    } catch {
      toast.error('Failed to generate invoice');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      accepted: 'primary',
      packed: 'primary',
      shipped: 'primary',
      delivered: 'success',
      rejected: 'danger',
      cancelled: 'danger'
    };
    return colors[status] || 'neutral';
  };

  const filteredOrders = orders.filter(order => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        order.marketplace_order_id?.toLowerCase().includes(search) ||
        order.customer_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const orderColumns = [
    { key: 'marketplace_order_id', label: 'Order ID', render: v => <span className="font-mono text-sm font-medium">{v}</span> },
    { key: 'customer_name', label: 'Customer', render: (v, row) => (
      <div><p className="font-medium text-[#0A0A0A]">{v}</p><p className="text-xs text-[#5a5c87] truncate max-w-[200px]">{row.customer_address}</p></div>
    )},
    { key: 'items', label: 'Items', render: v => <span className="text-sm">{v?.length || 0} item(s)</span> },
    { key: 'total_amount', label: 'Amount', render: v => formatCurrency(v) },
    { key: 'fulfillment_mode', label: 'Mode', render: v => <span className="text-xs font-semibold uppercase tracking-wider px-2 py-1 bg-[#fcfcfc] rounded">{v}</span> },
    { key: 'status', label: 'Status', render: v => <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded sp-badge-${getStatusColor(v)}`}><span className={`status-dot ${getStatusColor(v)}`}></span>{v}</span> },
    { key: 'created_at', label: 'Date', render: v => formatDate(v) },
    { key: 'order_id', label: 'Actions', sortable: false, render: (v, row) => (
      <div className="flex gap-2">
        <Button variant="ghost" size="sm" onClick={() => openOrderDetail(row)} data-testid={`view-order-${v}`}><Eye size={16} /></Button>
        {row.status === 'pending' && (
          <>
            <Button variant="ghost" size="sm" className="text-[#28cd8c]" onClick={() => handleOrderAction(v, 'accept')} data-testid={`accept-order-${v}`}><Check size={16} /></Button>
            <Button variant="ghost" size="sm" className="text-[#ea4b5a]" onClick={() => handleOrderAction(v, 'reject')} data-testid={`reject-order-${v}`}><X size={16} /></Button>
          </>
        )}
        {row.status === 'accepted' && <Button variant="ghost" size="sm" className="text-[#7d64ff]" onClick={() => handleOrderAction(v, 'pack')} data-testid={`pack-order-${v}`}>Pack</Button>}
        {row.status === 'packed' && <Button variant="ghost" size="sm" className="text-[#7d64ff]" onClick={() => { setShipOrderId(v); setShipForm({ tracking_number: '', courier: '' }); setShipDialogOpen(true); }} data-testid={`ship-order-${v}`}><Truck size={16} className="mr-1" /> Ship</Button>}
      </div>
    )}
  ];

  return (
    <DashboardLayout title="Orders" subtitle="Manage your marketplace orders">
      {/* Filters */}
      <div className="bg-white border border-[#e5e5ea] rounded-xl mb-5">
        <div className="action-bar">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9c9db7]" size={16} />
            <Input placeholder="Search by Order ID or Customer..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="pl-9 h-9 text-sm" data-testid="order-search" />
          </div>
          <Select value={filters.status || "all"} onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? "" : value })}>
            <SelectTrigger className="w-36 h-9" data-testid="status-filter"><Funnel size={14} className="mr-1.5 text-[#9c9db7]" /><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="packed">Packed</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.fulfillment_mode || "all"} onValueChange={(value) => setFilters({ ...filters, fulfillment_mode: value === "all" ? "" : value })}>
            <SelectTrigger className="w-36 h-9" data-testid="mode-filter"><SelectValue placeholder="Mode" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Modes</SelectItem>
              <SelectItem value="dropship">Dropship</SelectItem>
              <SelectItem value="selfship">Selfship</SelectItem>
              <SelectItem value="b2b2c">B2B2C</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner"><div className="spinner"></div></div>
      ) : (
        <SortableTable columns={orderColumns} data={filteredOrders} pageSize={15} emptyMessage="No orders found" emptyIcon={Package} />
      )}

      {/* Order Detail Drawer */}
      <Sheet open={!!selectedOrder} onOpenChange={closeDrawer}>
        <SheetContent side="right" className="w-full sm:max-w-[520px] p-0 overflow-y-auto" data-testid="order-detail-drawer">
          {selectedOrder && (
            <>
              {/* Header */}
              <div className="sticky top-0 bg-white z-10 border-b border-[#e5e5ea] px-6 py-4">
                <SheetHeader>
                  <div className="flex items-center justify-between pr-8">
                    <SheetTitle className="text-lg font-bold text-[#0A0A0A]">{selectedOrder.marketplace_order_id}</SheetTitle>
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                      selectedOrder.status === 'delivered' ? 'bg-emerald-50 text-emerald-700' :
                      selectedOrder.status === 'shipped' ? 'bg-violet-50 text-violet-700' :
                      selectedOrder.status === 'pending' ? 'bg-amber-50 text-amber-700' :
                      selectedOrder.status === 'accepted' ? 'bg-blue-50 text-blue-700' :
                      selectedOrder.status === 'packed' ? 'bg-indigo-50 text-indigo-700' :
                      'bg-red-50 text-red-700'
                    }`} data-testid="order-status-badge">{selectedOrder.status}</span>
                  </div>
                  <p className="text-xs text-[#9c9db7] mt-0.5">
                    Order ID: <button onClick={() => copyToClipboard(selectedOrder.order_id)} className="font-mono hover:text-[#7d64ff] inline-flex items-center gap-1">{selectedOrder.order_id} <Copy size={10} /></button>
                  </p>
                </SheetHeader>
              </div>

              {detailLoading ? (
                <div className="p-6 space-y-4">
                  <div className="h-24 skeleton rounded-xl" />
                  <div className="h-20 skeleton rounded-xl" />
                  <div className="h-40 skeleton rounded-xl" />
                  <div className="h-24 skeleton rounded-xl" />
                </div>
              ) : (
                <div className="p-6 space-y-5">
                  {/* Timeline */}
                  <div className="flex items-center gap-2 text-[10px] text-[#9c9db7]">
                    <CalendarBlank size={12} />
                    <span>Created {formatDate((orderDetail || selectedOrder).created_at)}</span>
                    {(orderDetail || selectedOrder).updated_at !== (orderDetail || selectedOrder).created_at && (
                      <><span>&middot;</span><span>Updated {formatDate((orderDetail || selectedOrder).updated_at)}</span></>
                    )}
                  </div>

                  {/* Order Summary */}
                  <div className="grid grid-cols-3 gap-3" data-testid="order-summary-cards">
                    <div className="bg-[#f9f9fb] rounded-xl p-3 text-center">
                      <p className="text-[10px] font-semibold text-[#9c9db7] uppercase tracking-wider">Total</p>
                      <p className="text-lg font-bold text-[#0A0A0A] mt-0.5">{formatCurrency((orderDetail || selectedOrder).total_amount)}</p>
                    </div>
                    <div className="bg-[#f9f9fb] rounded-xl p-3 text-center">
                      <p className="text-[10px] font-semibold text-[#9c9db7] uppercase tracking-wider">Mode</p>
                      <p className="text-sm font-bold text-[#0A0A0A] mt-1 uppercase">{(orderDetail || selectedOrder).fulfillment_mode}</p>
                    </div>
                    <div className="bg-[#f9f9fb] rounded-xl p-3 text-center">
                      <p className="text-[10px] font-semibold text-[#9c9db7] uppercase tracking-wider">Items</p>
                      <p className="text-lg font-bold text-[#0A0A0A] mt-0.5">{(orderDetail || selectedOrder).items?.length || 0}</p>
                    </div>
                  </div>

                  {/* Customer Details */}
                  <div className="bg-white border border-[#e5e5ea] rounded-xl overflow-hidden" data-testid="order-customer-info">
                    <div className="px-4 py-2.5 bg-[#f9f9fb] border-b border-[#e5e5ea]">
                      <p className="text-[11px] font-semibold text-[#6b6b8a] uppercase tracking-wider">Customer</p>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#7d64ff]/10 flex items-center justify-center flex-shrink-0">
                          <User size={14} className="text-[#7d64ff]" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#0A0A0A]">{(orderDetail || selectedOrder).customer_name}</p>
                          <p className="text-xs text-[#9c9db7]">{(orderDetail || selectedOrder).customer_phone}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#28cd8c]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <MapPin size={14} className="text-[#28cd8c]" />
                        </div>
                        <p className="text-sm text-[#3c3c64] leading-relaxed">{(orderDetail || selectedOrder).customer_address}</p>
                      </div>
                    </div>
                  </div>

                  {/* Items */}
                  <div className="bg-white border border-[#e5e5ea] rounded-xl overflow-hidden" data-testid="order-items-list">
                    <div className="px-4 py-2.5 bg-[#f9f9fb] border-b border-[#e5e5ea] flex items-center justify-between">
                      <p className="text-[11px] font-semibold text-[#6b6b8a] uppercase tracking-wider">Items</p>
                      <p className="text-[11px] text-[#9c9db7]">{(orderDetail || selectedOrder).items?.length || 0} item(s)</p>
                    </div>
                    <div className="divide-y divide-[#f0f0f0]">
                      {(orderDetail || selectedOrder).items?.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between px-4 py-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-lg bg-[#f2f0ff] flex items-center justify-center flex-shrink-0">
                              <Package size={16} className="text-[#7d64ff]" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-[#0A0A0A] truncate">{item.name || item.product_id}</p>
                              <p className="text-[11px] text-[#9c9db7]">Qty: {item.quantity} &times; {formatCurrency(item.price)}</p>
                            </div>
                          </div>
                          <p className="text-sm font-semibold text-[#0A0A0A] flex-shrink-0 ml-3">{formatCurrency(item.price * item.quantity)}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Financials */}
                  <div className="bg-white border border-[#e5e5ea] rounded-xl overflow-hidden" data-testid="order-financials">
                    <div className="px-4 py-2.5 bg-[#f9f9fb] border-b border-[#e5e5ea]">
                      <p className="text-[11px] font-semibold text-[#6b6b8a] uppercase tracking-wider">Financials</p>
                    </div>
                    <div className="p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-[#6b6b8a]">Subtotal</span>
                        <span className="font-medium">{formatCurrency((orderDetail || selectedOrder).total_amount)}</span>
                      </div>
                      {(orderDetail || selectedOrder).commission_rate > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-[#6b6b8a]">Commission Rate</span>
                          <span className="text-[#9c9db7]">{(orderDetail || selectedOrder).commission_rate}%</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-[#6b6b8a]">Commission</span>
                        <span className="text-[#ea4b5a] font-medium">-{formatCurrency((orderDetail || selectedOrder).commission_amount)}</span>
                      </div>
                      <div className="border-t border-[#f0f0f0] pt-2 mt-2 flex justify-between">
                        <span className="text-sm font-semibold text-[#0A0A0A]">Net Payout</span>
                        <span className="text-base font-bold text-[#28cd8c]">{formatCurrency(((orderDetail || selectedOrder).total_amount || 0) - ((orderDetail || selectedOrder).commission_amount || 0))}</span>
                      </div>
                    </div>
                  </div>

                  {/* Shipping & Tracking */}
                  {((orderDetail || selectedOrder).tracking_number || (orderDetail || selectedOrder).courier || (orderDetail || selectedOrder).logistics_provider) && (
                    <div className="bg-white border border-[#e5e5ea] rounded-xl overflow-hidden" data-testid="order-shipping-info">
                      <div className="px-4 py-2.5 bg-[#f9f9fb] border-b border-[#e5e5ea]">
                        <p className="text-[11px] font-semibold text-[#6b6b8a] uppercase tracking-wider">Shipping</p>
                      </div>
                      <div className="p-4 space-y-2">
                        {(orderDetail || selectedOrder).courier && (
                          <div className="flex justify-between text-sm">
                            <span className="text-[#6b6b8a]">Courier</span>
                            <span className="font-medium">{(orderDetail || selectedOrder).courier}</span>
                          </div>
                        )}
                        {(orderDetail || selectedOrder).logistics_provider && (
                          <div className="flex justify-between text-sm">
                            <span className="text-[#6b6b8a]">Logistics</span>
                            <span className="font-medium">{(orderDetail || selectedOrder).logistics_provider}</span>
                          </div>
                        )}
                        {(orderDetail || selectedOrder).tracking_number && (
                          <div className="flex justify-between text-sm items-center">
                            <span className="text-[#6b6b8a]">Tracking</span>
                            <button onClick={() => copyToClipboard((orderDetail || selectedOrder).tracking_number)} className="font-mono font-medium hover:text-[#7d64ff] inline-flex items-center gap-1">
                              {(orderDetail || selectedOrder).tracking_number} <Copy size={10} />
                            </button>
                          </div>
                        )}
                        {(orderDetail || selectedOrder).store_id && (
                          <div className="flex justify-between text-sm">
                            <span className="text-[#6b6b8a]">Store</span>
                            <span className="font-mono text-xs">{(orderDetail || selectedOrder).store_id}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* OTP Verification */}
                  {(orderDetail || selectedOrder).otp_verified && (
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700" data-testid="order-otp-verified">
                      <Check size={16} weight="bold" />
                      <span className="font-medium">OTP Verified</span>
                      {(orderDetail || selectedOrder).delivered_at && <span className="text-xs ml-auto">{formatDate((orderDetail || selectedOrder).delivered_at)}</span>}
                    </div>
                  )}

                  {/* Hop History */}
                  {(orderDetail || selectedOrder).hop_history?.length > 0 && (
                    <div className="bg-white border border-[#e5e5ea] rounded-xl overflow-hidden" data-testid="order-hop-history">
                      <div className="px-4 py-2.5 bg-[#f9f9fb] border-b border-[#e5e5ea]">
                        <p className="text-[11px] font-semibold text-[#6b6b8a] uppercase tracking-wider">Hop History</p>
                      </div>
                      <div className="divide-y divide-[#f0f0f0]">
                        {(orderDetail || selectedOrder).hop_history.map((hop, i) => (
                          <div key={i} className="px-4 py-2.5 flex items-center gap-3 text-xs">
                            <ArrowsClockwise size={14} className="text-[#7d64ff] flex-shrink-0" />
                            <div>
                              <p className="text-[#3c3c64]"><span className="font-mono">{hop.from_store}</span> → <span className="font-mono font-semibold">{hop.to_store}</span></p>
                              <p className="text-[#9c9db7]">{hop.reason} &middot; {new Date(hop.hopped_at).toLocaleString()}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="space-y-2 pt-1" data-testid="order-actions">
                    {selectedOrder.status === 'pending' && (
                      <div className="grid grid-cols-2 gap-2">
                        <Button onClick={() => handleOrderAction(selectedOrder.order_id, 'accept')} disabled={actionLoading} className="bg-[#28cd8c] hover:bg-[#1fb076] text-white" data-testid="drawer-accept-btn">
                          <Check size={16} className="mr-1.5" /> Accept
                        </Button>
                        <Button onClick={() => handleOrderAction(selectedOrder.order_id, 'reject')} disabled={actionLoading} variant="outline" className="border-[#ea4b5a] text-[#ea4b5a] hover:bg-[#ea4b5a]/5" data-testid="drawer-reject-btn">
                          <X size={16} className="mr-1.5" /> Reject
                        </Button>
                      </div>
                    )}
                    {selectedOrder.status === 'accepted' && (
                      <Button onClick={() => handleOrderAction(selectedOrder.order_id, 'pack')} disabled={actionLoading} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef] text-white" data-testid="drawer-pack-btn">
                        <Package size={16} className="mr-1.5" /> Mark as Packed
                      </Button>
                    )}
                    {selectedOrder.status === 'packed' && (
                      <Button onClick={() => { closeDrawer(); setShipOrderId(selectedOrder.order_id); setShipForm({ tracking_number: '', courier: '' }); setShipDialogOpen(true); }} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef] text-white" data-testid="drawer-ship-btn">
                        <Truck size={16} className="mr-1.5" /> Ship Order
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => downloadInvoice(selectedOrder.order_id)} className="w-full border-[#7d64ff] text-[#7d64ff] hover:bg-[#f8f7ff]" data-testid="download-invoice-btn">
                      <FilePdf size={16} className="mr-1.5" /> Download Invoice
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Ship Order Dialog */}
      <Dialog open={shipDialogOpen} onOpenChange={setShipDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-bold">Ship Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Tracking Number *</Label>
              <Input
                value={shipForm.tracking_number}
                onChange={e => setShipForm({ ...shipForm, tracking_number: e.target.value })}
                placeholder="Enter tracking number"
                className="mt-1 rounded-sm font-mono"
                data-testid="ship-tracking-number"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Courier Service</Label>
              <Select value={shipForm.courier || "none"} onValueChange={v => setShipForm({ ...shipForm, courier: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="ship-courier">
                  <SelectValue placeholder="Select courier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select courier</SelectItem>
                  <SelectItem value="Delhivery">Delhivery</SelectItem>
                  <SelectItem value="BlueDart">BlueDart</SelectItem>
                  <SelectItem value="DTDC">DTDC</SelectItem>
                  <SelectItem value="Ecom Express">Ecom Express</SelectItem>
                  <SelectItem value="Xpressbees">Xpressbees</SelectItem>
                  <SelectItem value="India Post">India Post</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-4 pt-2">
              <Button variant="outline" onClick={() => setShipDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button
                className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white"
                disabled={!shipForm.tracking_number.trim() || actionLoading}
                onClick={async () => {
                  await handleOrderAction(shipOrderId, 'ship', shipForm);
                  setShipDialogOpen(false);
                }}
                data-testid="confirm-ship-btn"
              >
                {actionLoading ? 'Shipping...' : 'Confirm Shipment'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
